import React, { useState, useEffect, useMemo } from 'react';
import { X, User, Building2 } from 'lucide-react';
import { Contact } from '../types';
import { useVersionTracking } from '../hooks/useVersionTracking';
import { usersService, contactsService } from '../services/supabaseService';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (contact: Partial<Contact>) => void;
  contact?: Contact | null;
  // Cuando se abre para crear, permite forzar el tipo (por ejemplo desde el
  // filtro "Empresas" del listado). Default: 'person'.
  defaultEntityType?: 'person' | 'company';
}

interface FormState {
  entityType: 'person' | 'company';
  fullName: string;
  email: string;
  countryCode: string;
  phoneNumber: string;
  role: string;
  parentId: string;               // vacío = sin empresa asociada
  leadStatus: Contact['leadStatus'];
  productInterest: string;
  owner: string;
  notes: string;
  website: string;
  logo: string;
}

const COUNTRY_CODES = [
  { code: '+34', country: 'España', flag: '🇪🇸' },
  { code: '+1', country: 'Estados Unidos', flag: '🇺🇸' },
  { code: '+44', country: 'Reino Unido', flag: '🇬🇧' },
  { code: '+33', country: 'Francia', flag: '🇫🇷' },
  { code: '+49', country: 'Alemania', flag: '🇩🇪' },
  { code: '+39', country: 'Italia', flag: '🇮🇹' },
  { code: '+351', country: 'Portugal', flag: '🇵🇹' },
  { code: '+54', country: 'Argentina', flag: '🇦🇷' },
  { code: '+52', country: 'México', flag: '🇲🇽' },
  { code: '+57', country: 'Colombia', flag: '🇨🇴' },
  { code: '+56', country: 'Chile', flag: '🇨🇱' },
  { code: '+51', country: 'Perú', flag: '🇵🇪' },
  { code: '+598', country: 'Uruguay', flag: '🇺🇾' },
  { code: '+58', country: 'Venezuela', flag: '🇻🇪' },
  { code: '+593', country: 'Ecuador', flag: '🇪🇨' },
  { code: '+55', country: 'Brasil', flag: '🇧🇷' },
];

const parsePhoneNumber = (phone: string): { countryCode: string; phoneNumber: string } => {
  if (!phone) return { countryCode: '+34', phoneNumber: '' };
  const match = COUNTRY_CODES.find(cc => phone.startsWith(cc.code));
  if (match) {
    return { countryCode: match.code, phoneNumber: phone.substring(match.code.length).trim() };
  }
  return { countryCode: '+34', phoneNumber: phone };
};

const emptyState = (entityType: 'person' | 'company'): FormState => ({
  entityType,
  fullName: '',
  email: '',
  countryCode: '+34',
  phoneNumber: '',
  role: '',
  parentId: '',
  leadStatus: 'nuevo',
  productInterest: '',
  owner: '',
  notes: '',
  website: '',
  logo: '',
});

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose, onSave, contact, defaultEntityType = 'person' }) => {
  const { trackContactAction } = useVersionTracking();

  const [formData, setFormData] = useState<FormState>(emptyState(defaultEntityType));
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [availableCompanies, setAvailableCompanies] = useState<Contact[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isCompany = formData.entityType === 'company';

  useEffect(() => {
    if (isOpen) loadDropdownData();
  }, [isOpen]);

  useEffect(() => {
    if (contact) {
      const { countryCode, phoneNumber } = parsePhoneNumber(contact.phone);
      setFormData({
        entityType: contact.entityType || 'person',
        fullName: contact.fullName,
        email: contact.email,
        countryCode,
        phoneNumber,
        role: contact.role || '',
        parentId: contact.parentId || '',
        leadStatus: contact.leadStatus,
        productInterest: contact.productInterest,
        owner: contact.owner,
        notes: contact.notes,
        website: contact.website || '',
        logo: contact.logo || '',
      });
    } else {
      setFormData(emptyState(defaultEntityType));
    }
    setErrors({});
  }, [contact, defaultEntityType, isOpen]);

  const loadDropdownData = async () => {
    try {
      setLoadingData(true);
      const [users, companies] = await Promise.all([
        usersService.getAll(),
        contactsService.getCompanies(),
      ]);
      const activeUsers = users.filter((u: any) => u.isActive);
      setAvailableUsers(activeUsers);
      setAvailableCompanies(companies);

      if (!contact && activeUsers.length > 0) {
        setFormData(prev => ({ ...prev, owner: prev.owner || activeUsers[0].id }));
      }
    } catch (error) {
      console.error('Error loading dropdown data:', error);
      setAvailableUsers([]);
      setAvailableCompanies([]);
    } finally {
      setLoadingData(false);
    }
  };

  const handlePhoneNumberChange = (value: string) => {
    const cleanValue = value.replace(/[^\d\s-]/g, '');
    setFormData({ ...formData, phoneNumber: cleanValue });
  };

  const selectedCompany = useMemo(
    () => availableCompanies.find(c => c.id === formData.parentId) || null,
    [availableCompanies, formData.parentId]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = isCompany ? 'El nombre de la empresa es obligatorio' : 'El nombre completo es obligatorio';
    }

    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'El email no tiene un formato válido';
    }

    if (!isCompany) {
      if (!formData.email.trim()) newErrors.email = 'El email es obligatorio para personas';
      if (!formData.productInterest.trim()) newErrors.productInterest = 'El producto de interés es obligatorio';
      if (!formData.owner.trim()) newErrors.owner = 'El propietario es obligatorio';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsSubmitting(true);

    trackContactAction(contact ? 'update' : 'create', formData.fullName);

    const selectedUser = availableUsers.find(u => u.id === formData.owner);
    const ownerName = selectedUser ? selectedUser.name : formData.owner;

    const fullPhone = formData.phoneNumber
      ? `${formData.countryCode} ${formData.phoneNumber}`
      : '';

    // Los datos que se guardan dependen del tipo. Para empresas no tiene
    // sentido leadStatus/productInterest/owner de las personas.
    const contactData: Partial<Contact> = {
      entityType: formData.entityType,
      fullName: formData.fullName,
      email: formData.email,
      phone: fullPhone,
      notes: formData.notes,
    };

    if (isCompany) {
      contactData.company = '';
      contactData.role = undefined;
      contactData.leadStatus = 'ya_es_cliente';
      contactData.productInterest = '';
      contactData.owner = '';
      contactData.parentId = undefined;
      contactData.website = formData.website || undefined;
      contactData.logo = formData.logo || undefined;
    } else {
      contactData.company = selectedCompany ? selectedCompany.fullName : '';
      contactData.role = formData.role || undefined;
      contactData.leadStatus = formData.leadStatus;
      contactData.productInterest = formData.productInterest;
      contactData.owner = ownerName;
      contactData.parentId = formData.parentId || undefined;
    }

    onSave(contactData);
    setIsSubmitting(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {contact
              ? (isCompany ? 'Editar Empresa' : 'Editar Cliente')
              : (isCompany ? 'Nueva Empresa' : 'Nuevo Cliente')}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Toggle Persona / Empresa (sólo al crear, no al editar) */}
        {!contact && (
          <div className="px-6 pt-4">
            <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, entityType: 'person' }))}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  !isCompany ? 'bg-white shadow text-blue-700' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <User className="w-4 h-4" />
                Persona
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, entityType: 'company' }))}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  isCompany ? 'bg-white shadow text-purple-700' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Building2 className="w-4 h-4" />
                Empresa
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {isCompany ? 'Nombre de la empresa *' : 'Nombre completo *'}
              </label>
              <input
                type="text"
                required
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.fullName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder={isCompany ? 'ej: Vervoer SL' : 'ej: Juan Pérez'}
              />
              {errors.fullName && <p className="text-red-600 text-xs mt-1">{errors.fullName}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email{isCompany ? '' : ' *'}
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder={isCompany ? 'contacto@empresa.com' : 'juan@empresa.com'}
              />
              {errors.email && <p className="text-red-600 text-xs mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
              <div className="flex space-x-2">
                <select
                  value={formData.countryCode}
                  onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {COUNTRY_CODES.map(cc => (
                    <option key={cc.code} value={cc.code}>{cc.flag} {cc.code}</option>
                  ))}
                </select>
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => handlePhoneNumberChange(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="666 123 456"
                />
              </div>
            </div>

            {isCompany ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sitio web</label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="https://empresa.com"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Logo (URL)</label>
                  <input
                    type="url"
                    value={formData.logo}
                    onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="https://…/logo.png"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label>
                  {loadingData ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2" />
                      <span className="text-gray-500 text-sm">Cargando empresas...</span>
                    </div>
                  ) : (
                    <select
                      value={formData.parentId}
                      onChange={(e) => setFormData({ ...formData, parentId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">— Sin empresa —</option>
                      {availableCompanies.map(c => (
                        <option key={c.id} value={c.id}>{c.fullName}</option>
                      ))}
                    </select>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Si la empresa no existe, créala primero desde el toggle "Empresa".
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cargo/Rol</label>
                  <input
                    type="text"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="ej: Director de IT"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado del Lead *</label>
                  <select
                    value={formData.leadStatus}
                    onChange={(e) => setFormData({ ...formData, leadStatus: e.target.value as Contact['leadStatus'] })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="nuevo">Nuevo</option>
                    <option value="en_seguimiento">En Seguimiento</option>
                    <option value="cerrado">Cerrado</option>
                    <option value="descartado">Descartado</option>
                    <option value="ya_es_cliente">Ya es Cliente</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Producto de Interés *</label>
                  <select
                    value={formData.productInterest}
                    onChange={(e) => setFormData({ ...formData, productInterest: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      errors.productInterest ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    required
                  >
                    <option value="">Seleccionar producto</option>
                    <option value="Desarrollo Web">Desarrollo Web</option>
                    <option value="Desarrollo IA">Desarrollo IA</option>
                    <option value="Desarrollo CRM">Desarrollo CRM</option>
                    <option value="Desarrollo ERP">Desarrollo ERP</option>
                    <option value="Branding y Marketing">Branding y Marketing</option>
                    <option value="E-commerce">E-commerce</option>
                    <option value="Consultoría Digital">Consultoría Digital</option>
                  </select>
                  {errors.productInterest && <p className="text-red-600 text-xs mt-1">{errors.productInterest}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Responsable *</label>
                  {loadingData ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2" />
                      <span className="text-gray-500 text-sm">Cargando usuarios...</span>
                    </div>
                  ) : (
                    <select
                      value={formData.owner}
                      onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        errors.owner ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                      required
                    >
                      <option value="">Seleccionar responsable</option>
                      {availableUsers.map(u => (
                        <option key={u.id} value={u.id}>
                          {u.name} ({u.role === 'superadmin' ? 'Admin' : 'Usuario'})
                        </option>
                      ))}
                    </select>
                  )}
                  {errors.owner && <p className="text-red-600 text-xs mt-1">{errors.owner}</p>}
                </div>
              </>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notas internas</label>
            <textarea
              rows={4}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder={isCompany ? 'Notas sobre la empresa…' : 'Notas sobre el cliente…'}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                isCompany ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {isSubmitting ? 'Guardando...' : (contact ? 'Actualizar' : (isCompany ? 'Crear empresa' : 'Crear cliente'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactModal;
